import * as React from 'react'

import { ProjectAccessType, UserAccountPosition } from '~/client/graph'
import type {
  TableFieldDef,
  TableFields,
} from '~/client/src/desktop/tables/tableFields'
import {
  buildRecordColumn,
  buildTableFields,
} from '~/client/src/desktop/tables/tableFields'
import TagListCell from '~/client/src/desktop/tables/TagListCell'
import { getAccountTypeTranslate } from '~/client/src/shared/constants/ProjectRoles'
import { getAccountPositionTranslate } from '~/client/src/shared/enums/AccountPosition'
import {
  getInviteStatusTranslate,
  InviteStatusCaption,
} from '~/client/src/shared/enums/InviteStatusCaption'
import { normalizeUnassigned } from '~/client/src/shared/lists/core'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { ITag } from '~/client/src/shared/models/Tag'
import User from '~/client/src/shared/models/User'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import {
  UserRowActionHandlers,
  UserRowActionsMenu,
} from './compound/UserRowActions'
import { GROUPING_COLUMN_ID, IUserRow, UserDirectoryColumnId } from './types'

interface UserFieldsArgs {
  readonly sortedCompanyIds: readonly string[]
  readonly tagsStore: TagsStore
}

function getAccountTypeOptionLabel(value: string): string {
  return getAccountTypeTranslate(value as ProjectAccessType)
}

function getInviteStatusOptionLabel(value: string): string {
  return getInviteStatusTranslate(value as InviteStatusCaption)
}

function resolveTags(tagsStore: TagsStore, ids: string[]): ITag[] {
  return (ids ?? []).flatMap(id => {
    const tag = tagsStore.getTagById(id)
    return tag ? [tag] : []
  })
}

function resolveTagNames(tagsStore: TagsStore, ids: string[]): string {
  if (!ids || ids.length === 0) return ''
  return ids
    .map(id => tagsStore.getTagById(id)?.name)
    .filter(Boolean)
    .join(', ')
}

export function buildUserFields({
  sortedCompanyIds,
  tagsStore,
}: UserFieldsArgs): TableFields<IUserRow> {
  const t = Localization.translator
  const companyBandOrder = sortedCompanyIds.map(normalizeUnassigned)
  const fields: readonly TableFieldDef<IUserRow>[] = [
    {
      key: UserDirectoryColumnId.Name,
      label: t.name,
      value: row => row.fullName,
      sortable: true,
      column: {
        size: 200,
        cell: record => record.fullName || NO_VALUE,
      },
      fixed: true,
    },
    {
      key: UserDirectoryColumnId.JobTitle,
      label: t.globalRole,
      value: row => row.jobTitle,
      facetable: true,
      emptyOptionLabel: t.none,
      sortable: true,
      column: {
        size: 180,
        cell: record => record.jobTitle || NO_VALUE,
      },
    },
    {
      key: UserDirectoryColumnId.Email,
      label: t.email_noun,
      value: row => row.email,
      sortable: true,
      column: {
        size: 260,
        cell: record => record.email || NO_VALUE,
      },
    },
    {
      key: UserDirectoryColumnId.Phone,
      label: t.phone,
      value: row => row.phone,
      searchable: false,
      column: {
        size: 140,
        enableSorting: false,
        cell: record => record.phone || NO_VALUE,
      },
    },
    {
      key: UserDirectoryColumnId.Status,
      label: t.inviteStatus,
      value: row => row.status,
      searchText: row => getInviteStatusTranslate(row.status),
      labelValue: row => getInviteStatusOptionLabel(row.status),
      facetable: true,
      sortable: true,
      column: {
        size: 130,
        cell: record =>
          record.status ? getInviteStatusTranslate(record.status) : NO_VALUE,
      },
    },
    {
      key: UserDirectoryColumnId.AccountType,
      label: t.accountType,
      value: row => row.accountType,
      searchText: row => getAccountTypeTranslate(row.accountType),
      labelValue: row => getAccountTypeOptionLabel(row.accountType),
      facetable: true,
      sortable: true,
      column: {
        size: 130,
        cell: record =>
          record.accountType
            ? getAccountTypeTranslate(record.accountType)
            : NO_VALUE,
      },
    },
    {
      key: UserDirectoryColumnId.AccountPosition,
      label: t.accountPosition,
      value: row => row.accountPosition,
      searchable: false,
      sortable: true,
      column: {
        size: 130,
        cell: record =>
          record.accountPosition
            ? getAccountPositionTranslate(
                record.accountPosition as UserAccountPosition,
              )
            : NO_VALUE,
      },
    },
    {
      key: GROUPING_COLUMN_ID,
      label: t.companies,
      value: row => normalizeUnassigned(row.companyId),
      labelValue: row => row.companyName,
      emptyOptionLabel: User.getDefaultCompanyName(),
      groupable: true,
      searchable: false,
      bandOrder: () => companyBandOrder,
    },
    {
      key: UserDirectoryColumnId.Roles,
      label: t.projectRoles,
      value: row => row.rolesIds,
      searchable: false,
      column: {
        size: 220,
        enableSorting: false,
        cell: record => (
          <TagListCell tags={resolveTags(tagsStore, record.rolesIds)} />
        ),
      },
    },
    {
      key: UserDirectoryColumnId.Trades,
      label: t.projectTrades,
      value: row => row.tradesIds,
      searchable: false,
      column: {
        size: 180,
        enableSorting: false,
        cell: record => (
          <TagListCell tags={resolveTags(tagsStore, record.tradesIds)} />
        ),
      },
    },
    {
      key: UserDirectoryColumnId.Teams,
      label: t.teams,
      value: row => row.teamsIds,
      searchable: false,
      column: {
        size: 180,
        enableSorting: false,
        cell: record => (
          <TagListCell tags={resolveTags(tagsStore, record.teamsIds)} />
        ),
      },
    },
    {
      key: UserDirectoryColumnId.UserTags,
      label: t.userTags,
      value: row => row.userTagIds ?? [],
      // Facet options are tag ids; the label array is positional, so map each id
      // to its name in the same order.
      labelValue: row =>
        (row.userTagIds ?? []).map(id => tagsStore.getTagById(id)?.name ?? id),
      searchText: row => resolveTagNames(tagsStore, row.userTagIds),
      sortValue: row => resolveTagNames(tagsStore, row.userTagIds),
      facetable: true,
      emptyOptionLabel: t.none,
      sortable: true,
      column: {
        size: 180,
        enableSorting: false,
        cell: record => (
          <TagListCell tags={resolveTags(tagsStore, record.userTagIds)} />
        ),
      },
    },
    {
      key: UserDirectoryColumnId.EmployeeId,
      label: t.employeeIDNumber,
      value: row => row.employeeId,
      sortable: true,
      column: {
        size: 150,
        cell: record => record.employeeId || NO_VALUE,
      },
    },
  ]

  return buildTableFields({
    getId: row => row.id,
    fields,
    bandLevels: [{ fieldKey: GROUPING_COLUMN_ID }],
  })
}

export function buildUserActionsColumn(
  rowActionHandlers: UserRowActionHandlers,
) {
  return buildRecordColumn<IUserRow>(UserDirectoryColumnId.Actions, {
    header: '',
    size: 44,
    enableSorting: false,
    meta: { align: 'center' },
    cell: record => (
      <UserRowActionsMenu row={record} handlers={rowActionHandlers} />
    ),
  })
}
